import PollOutlinedIcon from '@mui/icons-material/PollOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { TPoll } from '@onesource/schemas';
import { useVotePoll } from '@onesource/services';
import { format } from 'date-fns';
import { useState } from 'react';
import { RiCheckLine } from 'react-icons/ri';

import { PollVotersDialog } from './poll-voters-dialog';

interface PollCardProps {
  poll: TPoll;
  postId: string;
}

const linkSx = {
  fontFamily: 'Figtree, sans-serif',
  fontSize: 12,
  fontWeight: 700,
  color: 'primary.main',
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
} as const;

/**
 * Renders a post's poll as a framed section: a voting UI (single-tap options
 * or multi-select with a Vote button) until the user votes or the poll
 * closes, then horizontal result bars with per-option percentages. Voting is
 * optimistic via `useVotePoll`.
 */
export const PollCard = ({ poll, postId }: PollCardProps) => {
  const theme = useTheme();
  const voteMutation = useVotePoll();

  const hasVoted = poll.userVotedOptionIds.length > 0;
  const isClosed = !!poll.closesAt && new Date(poll.closesAt).getTime() <= Date.now();
  // Show the voting UI only when the user hasn't voted and the poll is open.
  const [editing, setEditing] = useState(!hasVoted && !isClosed);
  const [selected, setSelected] = useState<string[]>(poll.userVotedOptionIds);
  const [votersOpen, setVotersOpen] = useState(false);

  const submitVote = async (optionIds: string[]) => {
    if (optionIds.length === 0 || voteMutation.isPending) {
      return;
    }
    setEditing(false);
    try {
      await voteMutation.mutateAsync({ pollId: poll.id, postId, optionIds });
    } catch {
      // Mutation rolls back the optimistic cache update; reopen for retry.
      setEditing(true);
    }
  };

  const toggleSelected = (optionId: string) => {
    setSelected(prev => (prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]));
  };

  const startEditing = () => {
    setSelected(poll.userVotedOptionIds);
    setEditing(true);
  };

  const totalVotes = poll.totalVotes;
  const votesLabel = `${totalVotes} ${totalVotes === 1 ? 'vote' : 'votes'}`;
  let meta = votesLabel;
  if (isClosed) {
    meta = `Final results · ${votesLabel}`;
  } else if (poll.closesAt) {
    meta = `${votesLabel} · closes ${format(new Date(poll.closesAt), 'MMM d, h:mm a')}`;
  }

  const isVoting = editing && !isClosed;
  const maxVotes = Math.max(...poll.options.map(o => o.voteCount));

  return (
    <Box
      sx={{
        mt: 1,
        p: '12px',
        borderRadius: '12px',
        border: '1px solid #ececec',
        bgcolor: '#fcfcfc',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      {/* Header — poll marker + live meta (votes / closing time) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <PollOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 12.5, fontWeight: 700, color: '#555' }}>
          Poll
        </Typography>
        <Typography sx={{ ml: 'auto', fontFamily: 'Figtree, sans-serif', fontSize: 12, color: '#909090' }}>
          {meta}
        </Typography>
      </Box>

      {isVoting ? (
        <>
          {/* Voting UI — clickable options (single-select votes instantly;
              multi-select collects a selection then submits via Vote). */}
          {poll.options.map(option => {
            const isChecked = selected.includes(option.id);
            return (
              <Box
                key={option.id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (poll.allowMultiple) {
                    toggleSelected(option.id);
                  } else {
                    setSelected([option.id]);
                    submitVote([option.id]);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  px: '12px',
                  py: '10px',
                  borderRadius: '10px',
                  border: `1px solid ${isChecked ? theme.palette.primary.main : '#e2e2e2'}`,
                  bgcolor: isChecked ? `${theme.palette.primary.main}0F` : 'white',
                  cursor: 'pointer',
                  transition: 'background-color .15s, border-color .15s, transform .1s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: isChecked ? `${theme.palette.primary.main}0F` : `${theme.palette.primary.main}08`,
                  },
                  '&:active': { transform: 'scale(0.995)' },
                }}
              >
                {poll.allowMultiple ? (
                  <Checkbox checked={isChecked} size="small" sx={{ p: 0 }} />
                ) : (
                  <Radio checked={isChecked} size="small" sx={{ p: 0 }} />
                )}
                <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 14, fontWeight: 500, color: '#333' }}>
                  {option.text}
                </Typography>
              </Box>
            );
          })}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: '2px' }}>
            {poll.allowMultiple && (
              <Button
                variant="contained"
                size="small"
                disabled={selected.length === 0 || voteMutation.isPending}
                onClick={() => submitVote(selected)}
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontFamily: 'Figtree, sans-serif',
                  fontWeight: 600,
                  px: 2.5,
                  '&.Mui-disabled': { backgroundColor: '#E0E0E0', color: '#9E9E9E' },
                }}
              >
                Vote
              </Button>
            )}
            {hasVoted && (
              <Typography onClick={() => setEditing(false)} sx={linkSx}>
                View results
              </Typography>
            )}
          </Box>
        </>
      ) : (
        <>
          {/* Results UI — animated bars with percentages; the user's picks and
              the leading option are emphasized. */}
          {poll.options.map(option => {
            const pct = totalVotes > 0 ? Math.round((option.voteCount / totalVotes) * 100) : 0;
            const voted = poll.userVotedOptionIds.includes(option.id);
            const isLeading = option.voteCount > 0 && option.voteCount === maxVotes;
            return (
              <Box
                key={option.id}
                sx={{
                  position: 'relative',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  border: `1px solid ${voted ? theme.palette.primary.main : '#e8e8e8'}`,
                  bgcolor: 'white',
                  px: '12px',
                  py: '9px',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: `${pct}%`,
                    bgcolor: voted ? `${theme.palette.primary.main}26` : isLeading ? '#e9e9e9' : '#f2f2f2',
                    transformOrigin: 'left',
                    '@keyframes pollBarIn': {
                      from: { transform: 'scaleX(0)' },
                      to: { transform: 'scaleX(1)' },
                    },
                    animation: 'pollBarIn 0.45s ease-out',
                    transition: 'width .3s ease',
                  }}
                />
                <Box
                  sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    {voted && <RiCheckLine size={16} color={theme.palette.primary.main} />}
                    <Typography
                      sx={{
                        fontFamily: 'Figtree, sans-serif',
                        fontSize: 14,
                        fontWeight: voted || isLeading ? 700 : 500,
                        color: '#333',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {option.text}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: 'Figtree, sans-serif',
                      fontSize: 13,
                      fontWeight: isLeading ? 700 : 600,
                      color: isLeading ? '#333' : '#555',
                    }}
                  >
                    {pct}%
                  </Typography>
                </Box>
              </Box>
            );
          })}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: '2px' }}>
            {totalVotes > 0 && (
              <Typography onClick={() => setVotersOpen(true)} sx={linkSx}>
                See who voted
              </Typography>
            )}
            {!isClosed && (
              <Typography onClick={startEditing} sx={linkSx}>
                {hasVoted ? 'Change vote' : 'Vote'}
              </Typography>
            )}
          </Box>
        </>
      )}
      <PollVotersDialog poll={poll} open={votersOpen} onClose={() => setVotersOpen(false)} />
    </Box>
  );
};
